// pages/WritingAnalysisResult.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import '../styles/writingAnalysisResult.css';

const WritingAnalysisResult = () => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get name from URL
  const name = decodeURIComponent(window.location.pathname.split('/').pop());

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const response = await axios.get(`https://n8n.iniai.cloud/webhook/get-single-writer?name=${encodeURIComponent(name)}`, {
          headers: {
            'x-api-key': 'auth_k_2f9a8b3c7d6e4f5g9h8i1j2k3l4m5n6'
          }
        });

        if (!response.data || response.data.length === 0) throw new Error('Analysis not found');
        setAnalysis(response.data[0]); // Take first result since we're filtering by name
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysis();
  }, [name]);

  if (loading) return <div className="loading-state">Analyzing your writing style...</div>;
  if (error) return <div className="error-state">{error}</div>;
  if (!analysis) return <div className="error-state">Analysis not found</div>;

  return (
    <div className="analysis-container">
      <h1>Writing Style Analysis</h1>

      <div className="page-navigation">
        <div className="breadcrumb">
          Writing Analysis / Results
        </div>
        <a href="/writinganalysis" className="back-link">← New Analysis</a>
      </div>

      <div className="meta-section">
        <div className="meta-row">
          <span className="meta-label">Writer:</span>
          <span className="meta-value">{analysis.Name}</span>
          
          <span className="meta-label">Primary Style:</span>
          <span className="meta-value style-tag">{analysis['Writing Style']}</span>
        </div>
      </div>

      <div className="analysis-sections">
        <div className="analysis-section">
          <h2>
            <LucideIcons.MessageSquare className="section-icon" />
            Voice Characteristics
          </h2>
          <ul className="characteristics-list">
            {analysis['Voice Characteristics']?.map((char, index) => (
              <li key={index} className="characteristic-item">
                <LucideIcons.Check className="list-icon" />
                {char}
              </li>
            ))}
          </ul>
        </div>

        <div className="analysis-section">
          <h2>
            <LucideIcons.Layout className="section-icon" />
            Structure Patterns
          </h2>
          <div className="structure-grid">
            <div className="structure-item">
              <h3>Transitions</h3>
              <ul>
                {analysis['Structure Transition Patterns']?.map((pattern, index) => (
                  <li key={index}>{pattern}</li>
                ))}
              </ul>
            </div>
            <div className="structure-item">
              <h3>Formatting</h3>
              <ul>
                {analysis['Structure Formatting Preferences']?.map((pref, index) => (
                  <li key={index}>{pref}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="analysis-section">
          <h2>
            <LucideIcons.Users className="section-icon" />
            Engagement Style
          </h2>
          <div className="engagement-details">
            <div className="engagement-style">
              <h3>Connection Style</h3>
              <p>{analysis['Engagement Connection Style']}</p>
            </div>
            <div className="engagement-triggers">
              <h3>Interaction Triggers</h3>
              <ul>
                {analysis['Engagement Interaction Triggers']?.map((trigger, index) => (
                  <li key={index}>{trigger}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="analysis-section">
          <h2>
            <LucideIcons.Languages className="section-icon" />
            Language Patterns
          </h2>
          <div className="language-details">
            <div className="industry-terms">
              <h3>Industry Terms</h3>
              <div className="terms-cloud">
                {analysis['Language Industry Terms']?.map((term, index) => (
                  <span key={index} className="term-tag">{term}</span>
                ))}
              </div>
            </div>
            {analysis['Language Emoji Patterns'] && (
              <div className="emoji-patterns">
                <h3>Emoji Usage</h3>
                <p>{analysis['Language Emoji Patterns']}</p>
              </div>
            )}
          </div>
        </div>

        <div className="analysis-section">
          <h2>
            <LucideIcons.FileText className="section-icon" />
            Sample Content
          </h2>
          <div className="samples-grid">
            {[1, 2, 3, 4, 5, 6, 7].map(num => {
              const content = analysis[`Sample Content ${num}`];
              if (!content) return null;
              return (
                <div key={num} className="sample-card">
                  <h3>Sample {num}</h3>
                  <div className="sample-content">{content}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WritingAnalysisResult;